// vg-binding: P7.D-15
// vg-binding: P7.D-16
// vg-binding: P7.D-17
//
// apps/vendor/src/features/products/variants/VariantMatrix.tsx
//
// Phase 7 Wave 8 Task 29 — variant matrix grid editor.
//
// Rows = sizes, columns = colors. Each cell is a MatrixCellEditor
// (enable + markup). A ColorImageGroup row below the grid edits the
// shared image URLs per color (image groups).

import { useState } from 'react';

import { useTranslation } from '../../../lib/i18n/index.js';
import { ColorImageGroup } from './ColorImageGroup.js';
import { MatrixCellEditor } from './MatrixCellEditor.js';
import {
  cellKey,
  ensureCells,
  type MatrixModel,
  type VariantDraft,
} from './types.js';

interface Props {
  value: MatrixModel;
  onChange: (next: MatrixModel) => void;
  /** R7-01 — server-rejected fields per cell (cellKey → field names). */
  fieldErrors?: Record<string, string[]>;
  /** D17 — product priced per SKU: cells edit cost_cents, not the markup. */
  perSku?: boolean;
}

export function VariantMatrix({ value, onChange, fieldErrors, perSku = false }: Props): JSX.Element {
  const { t } = useTranslation('products');
  const [pendingSize, setPendingSize] = useState('');
  const [pendingColor, setPendingColor] = useState('');

  function addSize(): void {
    const s = pendingSize.trim();
    if (!s || value.sizes.includes(s)) {
      setPendingSize('');
      return;
    }
    onChange(ensureCells({ ...value, sizes: [...value.sizes, s] }));
    setPendingSize('');
  }
  function addColor(): void {
    const c = pendingColor.trim();
    if (!c || value.colors.includes(c)) {
      setPendingColor('');
      return;
    }
    onChange(ensureCells({ ...value, colors: [...value.colors, c] }));
    setPendingColor('');
  }
  function removeSize(s: string): void {
    onChange({
      ...value,
      sizes: value.sizes.filter((x) => x !== s),
    });
  }
  function removeColor(c: string): void {
    onChange({
      ...value,
      colors: value.colors.filter((x) => x !== c),
    });
  }
  function updateCell(next: VariantDraft): void {
    onChange({
      ...value,
      cells: { ...value.cells, [cellKey(next.size, next.color)]: next },
    });
  }
  function setColorImages(color: string, urls: string[]): void {
    const updated = { ...value.cells };
    for (const size of value.sizes) {
      const k = cellKey(size, color);
      const existing = updated[k];
      if (existing) {
        updated[k] = { ...existing, image_urls: urls };
      }
    }
    onChange({ ...value, cells: updated });
  }

  // Pull image list from first cell of the color (image group is shared).
  function imagesForColor(color: string): string[] {
    const firstSize = value.sizes[0];
    if (!firstSize) return [];
    return value.cells[cellKey(firstSize, color)]?.image_urls ?? [];
  }

  return (
    <section
      data-testid="variant-matrix"
      className="flex flex-col gap-4 rounded-xl border border-pw-border bg-pw-bg p-4"
    >
      <header>
        <h3 className="text-sm font-semibold text-pw-text">{t('variants.title')}</h3>
        <p className="text-xs text-pw-text-muted">{t('variants.description')}</p>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1">
          <input
            type="text"
            value={pendingSize}
            onChange={(e) => setPendingSize(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addSize();
              }
            }}
            placeholder={t('variants.addSizePlaceholder')}
            className="h-8 rounded border border-pw-border bg-pw-bg px-2 text-xs focus:border-pw-primary focus:outline-none"
          />
          <button
            type="button"
            data-testid="variant-add-size"
            onClick={addSize}
            className="rounded bg-pw-primary px-2 py-1 text-xs font-semibold text-white hover:bg-pw-primary-strong"
          >
            {t('variants.addSizeButton')}
          </button>
        </div>
        <div className="flex items-center gap-1">
          <input
            type="text"
            value={pendingColor}
            onChange={(e) => setPendingColor(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addColor();
              }
            }}
            placeholder={t('variants.addColorPlaceholder')}
            className="h-8 rounded border border-pw-border bg-pw-bg px-2 text-xs focus:border-pw-primary focus:outline-none"
          />
          <button
            type="button"
            data-testid="variant-add-color"
            onClick={addColor}
            className="rounded bg-pw-primary px-2 py-1 text-xs font-semibold text-white hover:bg-pw-primary-strong"
          >
            {t('variants.addColorButton')}
          </button>
        </div>
      </div>

      {/* dbg-vendor-variant-no-feedback: render added size/color chips above
          the matrix so the user gets immediate confirmation that an option
          was accepted. Previously chips only appeared inside <table> which
          itself is gated on BOTH sizes.length > 0 AND colors.length > 0, so
          adding only one axis looked like the input got "swallowed". */}
      {(value.sizes.length > 0 || value.colors.length > 0) ? (
        <div
          data-testid="variant-pending-chips"
          className="flex flex-wrap gap-2 rounded-md border border-dashed border-pw-border bg-pw-surface px-3 py-2"
        >
          {value.sizes.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1">
              <span className="text-[10px] uppercase tracking-wider text-pw-text-muted">
                {t('variants.matrixHeader').split('\\').shift()?.trim() || 'size'}:
              </span>
              {value.sizes.map((s) => (
                <span
                  key={`chip-size-${s}`}
                  className="inline-flex items-center gap-1 rounded-full bg-pw-bg px-2 py-0.5 text-[11px] font-medium text-pw-text ring-1 ring-pw-border"
                >
                  {s}
                  <button
                    type="button"
                    data-testid={`variant-chip-remove-size-${s}`}
                    onClick={() => removeSize(s)}
                    className="text-pw-text-muted hover:text-red-700"
                    aria-label={`Remove size ${s}`}
                  >
                    ✕
                  </button>
                </span>
              ))}
            </div>
          ) : null}
          {value.colors.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1">
              <span className="text-[10px] uppercase tracking-wider text-pw-text-muted">
                {t('variants.matrixHeader').split('\\').pop()?.trim() || t('variants.colorLabel')}:
              </span>
              {value.colors.map((c) => (
                <span
                  key={`chip-color-${c}`}
                  className="inline-flex items-center gap-1 rounded-full bg-pw-bg px-2 py-0.5 text-[11px] font-medium text-pw-text ring-1 ring-pw-border"
                >
                  {c}
                  <button
                    type="button"
                    data-testid={`variant-chip-remove-color-${c}`}
                    onClick={() => removeColor(c)}
                    className="text-pw-text-muted hover:text-red-700"
                    aria-label={`Remove color ${c}`}
                  >
                    ✕
                  </button>
                </span>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {value.sizes.length === 0 || value.colors.length === 0 ? (
        <p className="rounded-md border border-dashed border-pw-border bg-pw-surface px-3 py-4 text-center text-xs text-pw-text-muted">
          {t('variants.emptyHint')}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full table-auto border-collapse text-xs">
            <thead>
              <tr>
                <th className="border border-pw-border bg-pw-surface p-1 text-left text-pw-text-muted">
                  {t('variants.matrixHeader')}
                </th>
                {value.colors.map((c) => (
                  <th
                    key={c}
                    className="border border-pw-border bg-pw-surface p-1 text-pw-text"
                  >
                    <div className="flex items-center justify-between gap-1">
                      <span>{c}</span>
                      <button
                        type="button"
                        data-testid={`variant-remove-color-${c}`}
                        onClick={() => removeColor(c)}
                        className="text-pw-text-muted hover:text-red-700"
                      >
                        ✕
                      </button>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {value.sizes.map((s) => (
                <tr key={s}>
                  <th className="border border-pw-border bg-pw-surface p-1 text-left text-pw-text">
                    <div className="flex items-center justify-between gap-1">
                      <span>{s}</span>
                      <button
                        type="button"
                        data-testid={`variant-remove-size-${s}`}
                        onClick={() => removeSize(s)}
                        className="text-pw-text-muted hover:text-red-700"
                      >
                        ✕
                      </button>
                    </div>
                  </th>
                  {value.colors.map((c) => {
                    const cell = value.cells[cellKey(s, c)];
                    if (!cell) return <td key={c} className="border border-pw-border p-1" />;
                    return (
                      <td key={c} className="border border-pw-border p-1 align-top">
                        <MatrixCellEditor cell={cell} onChange={updateCell} errors={fieldErrors?.[cellKey(s, c)]} perSku={perSku} />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {value.colors.length > 0 && value.sizes.length > 0 ? (
        <div>
          <h4 className="mb-2 text-sm font-semibold text-pw-text">{t('variants.imageGroupsTitle')}</h4>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            {value.colors.map((c) => (
              <ColorImageGroup
                key={c}
                color={c}
                urls={imagesForColor(c)}
                onChange={(urls) => setColorImages(c, urls)}
              />
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
}

export default VariantMatrix;
